import { useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useCart } from '../components/CartDrawer';
import DummyCheckoutFields from '../components/DummyCheckoutFields';
import FallbackImage from '../components/FallbackImage';
import { sampleProductsPath } from '../lib/paths';
import { getProducts } from '../lib/products';

function parseCartString(cartString) {
  return cartString
    .split(',')
    .map((part) => {
      const [variantId, quantity] = part.split(':');
      return {
        variantId,
        quantity: Number(quantity) || 1,
      };
    })
    .filter((item) => item.variantId);
}

export default function CartLinkPage() {
  const navigate = useNavigate();
  const { cartString = '' } = useParams();
  const { setProductQuantity } = useCart();
  const loaded = useRef(false);

  const { items, missingVariantIds } = useMemo(() => {
    const requestedItems = parseCartString(cartString);
    const products = getProducts();
    const resolved = requestedItems
      .map((item) => ({
        product: products.find((product) => product.variant_id === item.variantId),
        quantity: item.quantity,
      }))
      .filter((item) => Boolean(item.product));

    return {
      items: resolved,
      missingVariantIds: requestedItems
        .filter((item) => !products.some((product) => product.variant_id === item.variantId))
        .map((item) => item.variantId),
    };
  }, [cartString]);

  const subtotal = items.reduce((sum, item) => sum + item.product.mrp * item.quantity, 0);
  const total = items.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const discount = subtotal - total;

  useEffect(() => {
    document.title = 'Cart | Nyafaz Skincare';
  }, []);

  useEffect(() => {
    if (loaded.current || missingVariantIds.length) return;
    loaded.current = true;
    items.forEach((item) => setProductQuantity(item.product, item.quantity));
  }, [items, missingVariantIds.length, setProductQuantity]);

  function checkout() {
    navigate(sampleProductsPath('/checkout'));
  }

  return (
    <main className="page">
      <section className="hero">
        <h1>{missingVariantIds.length ? 'Checkout unavailable' : 'Your Cart'}</h1>
        <p>Products loaded from your checkout link.</p>
      </section>

      {missingVariantIds.length ? (
        <section className="section-card">
          <p className="muted">Unknown variant ID: {missingVariantIds.join(', ')}</p>
          <Link to={sampleProductsPath('/products')} className="btn">
            Back to Products
          </Link>
        </section>
      ) : (
        <div className="cart-layout">
          <section className="section-card" style={{ marginTop: 0 }}>
            {items.map((item) => (
              <article className="cart-row" key={item.product.variant_id}>
                <FallbackImage src={item.product.imageUrl} alt={item.product.name} />
                <div>
                  <h2 className="product-title">{item.product.name}</h2>
                  <p className="muted">
                    SKU: {item.product.sku} • Variant: {item.product.variant_id}
                  </p>
                  <p>
                    ₹{item.product.price} <span className="mrp">₹{item.product.mrp}</span>
                  </p>
                  <span className="badge">Qty: {item.quantity}</span>
                </div>
              </article>
            ))}
          </section>

          <aside className="section-card cart-summary" style={{ marginTop: 0 }}>
            <h2>Order Summary</h2>
            <p className="price-row">
              <span>MRP</span>
              <strong>₹{subtotal}</strong>
            </p>
            <p className="price-row">
              <span>Discount</span>
              <strong>-₹{discount}</strong>
            </p>
            <p className="price-row">
              <span>Total</span>
              <strong className="price">₹{total}</strong>
            </p>
            <DummyCheckoutFields />
            <button className="btn" type="button" style={{ width: '100%', marginTop: 16 }} onClick={checkout}>
              Checkout
            </button>
          </aside>
        </div>
      )}
    </main>
  );
}
